import "./ViewApplication.css";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import groupedcircles from "../../Assets/Images/circle-grp.png";
import axios from "axios";
import envConfig from "../../environment";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";
import { PDFDocument } from 'pdf-lib';
import storage from "@utils/storage";
import { useSocket } from "@utils/socket/socketContext";

function ViewApplication() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const applicationData = state?.applicationData;
    const offer = applicationData?.offer;
    const lender = offer?.lender;
    const borrower = applicationData?.borrower;
    const Id = storage.getUser();
    const user = Id.user;
    const [contractUrl, setContractUrl] = useState('');
    const [, setShowPdf] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [isSigned, setIsSigned] = useState(false);
    const { socket } = useSocket();
    const [btnLoading, setBtnLoading] = useState(false);
    const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchContract = async () => {
            try {
                const res = await axios.get(`${envConfig.apiUrl}/user/borrower-contract`, {
                    params: {
                        borrowerUuid: borrower.uuid,
                        offerId: offer.uuid,
                    },
                });

                if (res?.data?.fileUrl) {
                    setContractUrl(res.data.fileUrl);
                    if (res.data.stage === 'signed_by_borrower') {
                        setIsSigned(true);
                    }

                } else {
                    toast("No contract found for this application.");
                }
            } catch (error) {
                console.error("Error fetching contract", error);
            }
        };

        fetchContract();
    }, [borrower.uuid, offer.uuid]);


    const togglePdf = () => {
        setShowPdf((prev) => !prev);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setIsSigned(false);
            setSignatureDataUrl(null);
        }
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let drawing = true;
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);

        const draw = (moveEvent: MouseEvent) => {
            if (!drawing) return;
            ctx.lineTo(moveEvent.clientX - rect.left, moveEvent.clientY - rect.top);
            ctx.stroke();
        };

        const stopDrawing = () => {
            drawing = false;
            setIsSigned(true);
            setSignatureDataUrl(canvas.toDataURL());
            canvas.removeEventListener('mousemove', draw);
            canvas.removeEventListener('mouseup', stopDrawing);
        };

        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
    };
    const handleSubmitSignature = async () => {
        if (!signatureDataUrl || !contractUrl) return;

        setBtnLoading(true);
        try {
            const existingPdfBytes = await fetch(contractUrl).then(res => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const signatureImageBytes = await fetch(signatureDataUrl).then(res => res.arrayBuffer());
            const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

            const pages = pdfDoc.getPages();
            const lastPage = pages[pages.length - 1];

            const { width } = lastPage.getSize();
            const sigWidth = 150;
            const sigHeight = 50;

            lastPage.drawImage(signatureImage, {
                x: width - sigWidth - 110,
                y: 270,
                width: sigWidth,
                height: sigHeight,
            });

            const signedPdfBytes = await pdfDoc.save();
            const signedBlob = new Blob([signedPdfBytes], { type: 'application/pdf' });

            const formData = new FormData();
            formData.append('file', signedBlob, `Borrower_Signed_Contract.pdf`);
            formData.append('stage', 'signed_by_borrower');
            formData.append('offerId', offer.uuid);
            formData.append('borrowerUuid', borrower.uuid);
            formData.append('applicationId', applicationData.uuid);
            formData.append('lenderUuid', lender.uuid);

            await axios.post(`${envConfig.apiUrl}/user/upload-contract`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            toast.success('Contract signed and sent to lender!');

            const modal = Modal.getInstance(document.getElementById('signModal'));
            modal?.hide();

            const borrowerId = borrower?.uuid;
            const lenderId = lender?.uuid;
            const offerTitle = offer?.title;
            const notificationTitle = "Contract Signed by Borrower";
            const notificationMessage = `The "${user.firstName}" has signed and returned the contract for the Offer "${offerTitle}".`;

            await axios.post(
                `${envConfig.apiUrl}/notification/create-offer-notification`,
                {
                    senderId: borrowerId,
                    senderType: "borrower",
                    receiverId: lenderId,
                    receiverType: "lender",
                    title: notificationTitle,
                    notification: notificationMessage,
                    isGlobal: false,
                },
                {
                    headers: {
                        Authorization: `Bearer ${storage.getToken()}`,
                    },
                }
            );

            if (socket?.connected) {
                socket.emit("contract signed", {
                    lenderId,
                    borrowerId,
                    offerTitle,
                    notificationMessage,
                });
            }

            window.location.reload();
        } catch (err) {
            console.error('Error signing contract:', err);
            toast.error('Failed to upload signed contract.');
        } finally {
            setBtnLoading(false); // End loading
        }
    };

    return (
        <div className="content completed-applicant-section">
            <div className="content-wrapper">
                <div className="title-search-block d-flex align-items-center justify-content-between">
                    <div className="back-btn mb-4">
                        <button onClick={() => navigate(-1)} type="button" className="button-back">
                            Back
                        </button>
                    </div>
                </div>

                <div className="outer-box-wrapper completed-offers p-0">
                    <div className="completed-box position-relative overflow-hidden">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="m-0">{offer?.title}</h4>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="download-pdf"
                                    onClick={() => {
                                        togglePdf();
                                        const modal = new Modal(document.getElementById('pdfModal'));
                                        modal.show();
                                    }}
                                    disabled={!contractUrl}
                                >
                                    {contractUrl ? "View Contract" : "No Contract Found"}
                                </button>
                                {contractUrl && !isSigned && (
                                    <button
                                        type="button"
                                        className="download-pdf"
                                        onClick={() => {
                                            const modal = new Modal(document.getElementById('signModal'));
                                            modal.show();
                                        }}
                                    >
                                        Sign
                                    </button>
                                )}
                            </div>
                        </div>


                        <div className="loan-info d-flex align-items-center gap-3">
                            <div className="loan-content">
                                Loan Amount: <span>${offer?.loanAmount}</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="loan-content">
                                Interest: <span>{offer?.interestRate}%</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="loan-content">
                                Date: <span>{new Date(offer?.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="loan-content">
                                Loan Duration: <span>{offer?.loanDuration} days</span>
                            </div>
                        </div>

                        <div className="description">
                            <div className="loan-content">
                                Terms & Conditions: <span>{offer?.termsAndConditions}</span>
                            </div>
                        </div>

                        <div className="loan-info d-flex align-items-center gap-3">
                            <div className="loan-content">
                                Full Name: <span>{lender?.firstName} {lender?.lastName}</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="loan-content">
                                Email: <span>{lender?.email}</span>
                            </div>
                            {Id?.role !== "borrower" && (
                                <>
                                    <div className="vr-line"></div>
                                    <div className="loan-content">
                                        Phone Number: <span>{lender?.phoneNumber}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {Id?.role !== "borrower" && (
                            <div className="loan-info d-flex align-items-center gap-3">
                                <div className="loan-content">
                                    Country: <span>{lender?.country || 'N/A'}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="loan-content">
                                    State: <span>{lender?.state || 'N/A'}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="loan-content">
                                    City: <span>{lender?.city || 'N/A'}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="loan-content">
                                    Region: <span>{lender?.region || 'N/A'}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="loan-content">
                                    Pin Code: <span>{lender?.pinCode || 'N/A'}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="loan-content">
                                    Address: <span>{lender?.address || 'N/A'}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grouped-circle position-absolute">
                    <img src={groupedcircles} alt="" className="img-fluid" />
                </div>
            </div>
            <div className="modal fade" id="pdfModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered sidebar-modal">
                    <div className="modal-content-pdf">
                        <div className="delete-popup">
                            <div className="signature-section">
                                <h5 className="view-contact mt-3">View Contract</h5>
                                {contractUrl && (
                                    <div className="pdf-viewer">
                                        <iframe
                                            src={contractUrl}
                                            width="100%"
                                            height="600px"
                                            title="Loan Contract"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal fade" id="signModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered sidebar-modal">
                    <div className="modal-content">
                        <div className="delete-popup">
                            <div className="signature-section">
                                <h5 className="mb-3">Please sign below before creating Contract:</h5>
                                <canvas
                                    ref={canvasRef}
                                    width={300}
                                    height={150}
                                    style={{ border: '1px solid #ccc', borderRadius: '10px', marginBottom: '20px' }}
                                    onMouseDown={startDrawing}
                                />
                                <div className="confirmation-btn d-flex align-items-center">
                                    <button
                                        onClick={handleSubmitSignature}
                                        className="send-contract-button"
                                        disabled={!isSigned || btnLoading}
                                    >
                                        {btnLoading ? "Signing..." : "Sign Contract"}
                                    </button>
                                    <button onClick={clearSignature} className="download-pdf">
                                        Clear Signature
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            {/* Borrower Info Section */}
            <div className="settlement-box">
                <h6 className="m-0 settlement-date">Borrower Information</h6>
                <div className="applicant-info">
                    <div className="d-flex align-items-center justify-content-between settlement-block">
                        <div className="applicant-name-bloc d-flex align-items-center gap-3">
                            <div className="applicant-name-icon d-flex align-items-center justify-content-center">
                                <span className="text-capitalize">{borrower?.firstName?.[0]}</span>
                            </div>
                            <div className="applicant-name">
                                <span>{borrower?.firstName}</span> <span>{borrower?.lastName}</span>
                            </div>
                        </div>
                    </div>

                    <div className="applicant-details">
                        <div className="location d-flex align-items-center gap-3 mb-3">
                            <div className="applicant-infofields">
                                Email Address: <span>{borrower?.email || "N/A"}</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="applicant-infofields">
                                Contact Number: <span>{borrower?.phoneNumber || "N/A"}</span>
                            </div>
                        </div>

                        <div className="location d-flex align-items-center gap-3 mb-3">
                            <div className="applicant-infofields">
                                Country: <span>{borrower?.country || "N/A"}</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="applicant-infofields">
                                State: <span>{borrower?.state || "N/A"}</span>
                            </div>
                            <div className="vr-line"></div>
                            <div className="applicant-infofields">
                                City: <span>{borrower?.city || "N/A"}</span>
                            </div>
                        </div>

                        <div className="applicant-infofields mb-3">
                            Region: <span>{borrower?.region || "N/A"}</span>
                        </div>
                        <div className="applicant-infofields mb-3">
                            Pin Code: <span>{borrower?.pinCode || "N/A"}</span>
                        </div>
                        <div className="applicant-infofields mb-3">
                            Address: <span>{borrower?.address || "N/A"}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
}

export default ViewApplication;
